import { track } from 'src/lib/analytics/utils';
import type { DialogID, ScriptID } from './conservation.ruleset';
import type {
  PetEvolutionStage,
  PetSpeciesID,
  PetState,
} from 'src/replicant/pet';

/**
 * Ways a pet dialog can be triggered.
 */
export type DialogTriggerType =
  | 'tap'
  | 'auto'
  | 'care_blocker'
  | 'evolution'
  | 'visitor';

/**
 * Conversation asset versions exposed to analytics.
 */
export type DialogVersion = 'v1' | 'v2';

/**
 * Analytics properties shared by local and friend dialog starts.
 */
type DialogStartProperties = {
  dialog_id: DialogID;
  times_seen: number;
  isCareActionBlocked?: boolean;
  petSpecies: PetSpeciesID;
  petState: PetState;
  petEvolutionStage: PetEvolutionStage;
  triggerType: DialogTriggerType;
  dialogVersion: DialogVersion;
};

/**
 * Track a conversation script shown to the player.
 *
 * @param scriptId Script displayed to the player.
 */
export function trackDialogueContinueEvent(scriptId: ScriptID) {
  // Track the script displayed to the player.
  track('DialogueContinue', { scriptId });
}

/**
 * Track an answer saved from an interactive conversation script.
 *
 * @param eventProperties Answer and parent dialog metadata.
 */
export function trackDialogAnswerEvent(eventProperties: {
  dialogId: DialogID;
  scriptId: ScriptID;
  answerKey?: string;
}) {
  // Track the answer after it has been saved successfully.
  track('DialogAnswer', eventProperties);
}

/**
 * Sent when the player has started a dialog
 *
 * @param eventProperties.dialog_id ID of the dialog
 * @param eventProperties.times_seen Number of times the player has seen the dialog, starting from 1. Used to check if players see the same dialog too many times
 * @param eventProperties.withFriends Whether a friend's pet was together or not
 * @param eventProperties.friendID User ID of the friend player the player is visiting
 * @param eventProperties.friendPetID Pet ID (species) of the friend's pet
 * @param eventProperties.friendPetStomach Value of the stomach gauge of the friend's pet
 * @param eventProperties.friendPetLove Value of the love gauge of the friend's pet
 * @param eventProperties.friendDirtiness Value of the dirtiness of the friend's pet
 * @param eventProperties.friendUrine Value of the urine of of the friend's pet
 * @param eventProperties.friendStress Value of the stress of the friend's pet
 * @param eventProperties.friendToothAcidity Value of the tooth_acidity of the friend's pet
 * @param eventProperties.friendAge Age of the friend's pet
 * @param eventProperties.friendPlayerLevel Player level of the friend
 * @param eventProperties.visits Number of times the player has visited the friend including the current visit
 * @param eventProperties.visited Number of times the player has been visited by the friend
 * @param eventProperties.petSpecies Pet species used to select the dialog
 * @param eventProperties.petState Pet state used to select the dialog
 * @param eventProperties.petEvolutionStage Evolution stage of the dialog species
 * @param eventProperties.triggerType How the dialog was triggered
 * @param eventProperties.dialogVersion Dialog asset/content version used
 */
export function trackDialogStartEvent(
  eventProperties:
    | (DialogStartProperties & {
        withFriends: false;
      })
    | (DialogStartProperties & {
        withFriends: true;
        friendID: string;
        friendPetID: string | undefined;
        friendPetStomach: number;
        friendPetLove: number;
        friendDirtiness: number;
        friendUrine: number;
        friendStress: number;
        friendToothAcidity: number;
        friendAge: number;
        friendPlayerLevel: number;
        visits: number;
        visited: number;
      }),
) {
  // Track the dialog and its pet, trigger, and content version.
  track('DialogStart', eventProperties);
}
